import omit from 'lodash/omit';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { ProjectId } from 'api/models';
import { Operation } from 'api/permissions';
import { ProjectPhase } from 'api/project';
import { Form } from 'components/atoms/form';
import { Modal } from 'components/atoms/modal';
import { useCurrentUserProjectAccess } from 'hooks/permissions';
import { useTranslation } from 'hooks/use-translation';
import {
  UpdateProjectPhasePayload,
  useCreateProjectPhaseMutation,
  useDeleteProjectPhaseMutation,
  useUpdateProjectPhaseMutation,
} from 'queries/project-phase';
import { ProjectPhaseModalFooter } from './footer';
import { ProjectPhaseForm } from './form';

export const ProjectPhaseModal: FC<{
  projectId: ProjectId;
  projectPhase?: AtLeast<ProjectPhase, 'projectPhaseId'>;
  visible: boolean;
  onClose(): void;
}> = ({ projectId, projectPhase, visible, onClose }) => {
  const isEdit = !!projectPhase?.projectPhaseId;
  const { mutate: createPhase, isLoading: isCreating } =
    useCreateProjectPhaseMutation(projectId);
  const { mutate: updatePhase, isLoading: isUpdating } =
    useUpdateProjectPhaseMutation(projectId);
  const { mutateAsync: deletePhase } = useDeleteProjectPhaseMutation();
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [form] = Form.useForm();
  const { isProjectAdmin, permittedPhaseOperations } =
    useCurrentUserProjectAccess(projectId);
  const hasPhaseDeleteAccess =
    isProjectAdmin || permittedPhaseOperations.includes(Operation.Delete);

  const modalTitle = useTranslation(
    isEdit ? 'projects.phase.edit' : 'projects.phase.create',
  );

  const phase = useMemo(() => {
    return {
      projectPhaseName: projectPhase?.projectPhaseName,
      startDate: projectPhase?.startDate,
      dueDate: projectPhase?.dueDate,
      dateRange:
        projectPhase?.startDate && projectPhase?.dueDate
          ? [projectPhase?.startDate, projectPhase?.dueDate]
          : null,
      private: !!projectPhase?.private,
      projectPhaseId: projectPhase?.projectPhaseId,
    };
  }, [projectPhase]);

  const onCloseModal = () => {
    form.resetFields();

    onClose();
  };

  const handleFormSubmit = () => {
    const _projectPhase: Partial<ProjectPhase> = {
      ...phase,
      ...omit(form.getFieldsValue(), ['dateRange']),
      startDate: form.getFieldValue('dateRange')[0],
      dueDate: form.getFieldValue('dateRange')[1],
    };

    if (projectPhase && _projectPhase.projectPhaseId) {
      updatePhase(_projectPhase as UpdateProjectPhasePayload, {
        onSuccess: () => {
          onCloseModal();
        },
      });
    } else {
      createPhase(_projectPhase, {
        onSuccess: () => {
          onCloseModal();
        },
      });
    }
  };

  const checkIfSubmitIsDisabled = (_, allValues) => {
    const _values: any[] = Object.values(allValues);
    const values: any[] = [].concat(..._values); // flattens to catch falsy values in nested array

    setIsSubmitDisabled(
      values.includes(undefined) ||
        values.includes(null) ||
        values.includes(''),
    );
  };

  const handlePhaseDelete = () => {
    if (projectPhase) {
      return deletePhase({
        projectId,
        projectPhaseId: projectPhase.projectPhaseId,
      });
    }

    return false;
  };

  useEffect(() => {
    form.setFieldsValue(phase);

    checkIfSubmitIsDisabled({}, phase);
  }, [form, phase, visible]);

  return (
    <Modal
      destroyOnClose
      title={modalTitle}
      visible={visible}
      footer={
        <ProjectPhaseModalFooter
          isEdit={isEdit}
          hasPhaseDeleteAccess={hasPhaseDeleteAccess}
          isSaveDisabled={isSubmitDisabled}
          onCancel={onCloseModal}
          onSubmit={handleFormSubmit}
          onDelete={handlePhaseDelete}
          isLoading={isCreating || isUpdating}
        />
      }
      onCancel={onCloseModal}
    >
      <ProjectPhaseForm
        form={form}
        isEdit={isEdit}
        onValuesChange={checkIfSubmitIsDisabled}
      />
    </Modal>
  );
};
